import { css } from '@emotion/react';
import { useContext } from 'react';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import t from 'react-translate';
import { copyToClipboard } from 'shared/react-utils';
import { AngularContext } from 'react-app';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvAthenaFroala from 'athena/components/nv-athena-froala';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvIcon from 'shared/components/nv-icon';
import ParticipantPill from 'athena/components/participant-pill';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';


export const whoThisIsForContentDefaultValue = `<ul>
  <li>Open to all employees with an interest in mentoring others</li>
  <li>No prior mentoring experience required</li>
  <li>Able to commit time on a regular basis</li>
</ul>`;

export const whatToExpectContentDefaultValue = `<ul>
  <li>Meet 1:1 with your mentee on a regular cadence</li>
  <li>Share your experience and career insights</li>
  <li>Set your own capacity and availability</li>
  <li>Accept or decline mentee connection requests</li>
  <li>Meet 1-2 times per month for 30 minutes</li>
</ul>`;

export const whyJoinContentDefaultValue = `<ul>
  <li>Develop your coaching and leadership skills</li>
  <li>Expand your professional network</li>
  <li>Give back and support the growth of others</li>
  <li>Build your reputation as a subject matter expert</li>
</ul>`;

const styles = css``;

type RegistrationPageProps = {
  role: MentoringProgramRole;
};

const flyerRoleByProgramRole: Partial<Record<MentoringProgramRole, string>> = {
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR]: 'mentor',
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE]: 'mentee',
};

const RegistrationPage = ({ role }: RegistrationPageProps) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, name, description, releasedDate, endDate } = mentoringProgram || {};

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);

  const formattedDates = releasedDate || endDate
    ? [
        releasedDate ? moment(releasedDate).format('MMM D, YYYY') : null,
        endDate ? moment(endDate).format('MMM D, YYYY') : null,
      ]
        .filter(Boolean)
        .join(' – ')
    : null;

  const copyLink = () => {
    const registrationUrl = $state.href(
      'mentoring-program-flyer',
      { programId, role: flyerRoleByProgramRole[role] },
      { absolute: true },
    );
    copyToClipboard(registrationUrl);
  };

  return (
    <div css={styles} className='p-4 border-gray-5-1 border-radius-2'>
      {/* Card header */}
      <div className='d-flex align-items-center justify-content-between pb-4 border-bottom border-gray-5'>
        <div className='d-flex align-items-center'>
          <div className='heading-5 mr-2'>
            {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.TITLE()}
          </div>
          <ParticipantPill role={role} />
        </div>
        <Button
          variant='secondary'
          className='d-flex align-items-center'
          onClick={copyLink}
        >
          <NvIcon icon='link' size='smallest' className='mr-1' />
          {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.COPY_LINK()}
        </Button>
      </div>

      {/* Page Contents section */}
      <div className='mt-4'>
        <div className='bold mb-2'>
          {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.PAGE_CONTENTS()}
        </div>
        <div className='mb-4'>
          {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.PAGE_CONTENTS_DESCRIPTION()}
        </div>

        {/* Read-only program info table */}
        <div className='bg-color-gray-7 border-gray-5-1 border-radius-2 mb-4'>
          <div className={`d-flex p-3 ${(description || formattedDates) && 'border-bottom border-gray-5'}`}>
            <div className='w-25 gray-2 semi-bold'>
              {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.PROGRAM_NAME()}
            </div>
            <div className='w-75'>{name}</div>
          </div>
          {description && (
            <div className={`d-flex p-3 ${formattedDates && 'border-bottom border-gray-5'}`}>
              <div className='w-25 gray-2 semi-bold'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.PROGRAM_DESCRIPTION()}
              </div>
              <div className='w-75'>{description}</div>
            </div>
          )}
          {formattedDates && (
            <div className='d-flex p-3'>
              <div className='w-25 gray-2 semi-bold'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.PROGRAM_DATES()}
              </div>
              <div className='w-75'>{formattedDates}</div>
            </div>
          )}
        </div>

        {/* Customizable RTE sections */}
        {/* Who this is for */}
        <div className='mb-4'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='heading-5'>
              {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHO_THIS_IS_FOR()}
            </div>
            <NvCheckbox
              withForm
              name='includeWhoThisIsFor'
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
            />
          </div>
          <NvAthenaFroala
            name='whoThisIsForContent'
            className='w-100'
            scrollableContainer='body'
          />
        </div>

        {/* What to expect */}
        <div className='mb-4'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='heading-5'>
              {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHAT_TO_EXPECT()}
            </div>
            <NvCheckbox
              withForm
              name='includeWhatToExpect'
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
            />
          </div>
          <NvAthenaFroala
            name='whatToExpectContent'
            className='w-100'
            scrollableContainer='body'
          />
        </div>

        {/* Why join? */}
        <div>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='heading-5'>
              {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHY_JOIN()}
            </div>
            <NvCheckbox
              withForm
              name='includeWhyJoin'
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
            />
          </div>
          <NvAthenaFroala
            name='whyJoinContent'
            className='w-100'
            scrollableContainer='body'
          />
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
